import { FC, ReactNode, useState } from 'react'
import styled from 'styled-components'

import Icon from 'core/components/Icon'
import { formatColumnHeader } from 'core/components/lib/Table/formatColumnHeader'
import {
  fontSizeLabel,
  fontSizeValue,
  fontWeightMedium,
  fontWeightSemibold,
  radiusRow,
} from 'core/styles'
import { useDarkModeInfo } from 'core/theme/DarkModeProvider'

import {
  blockGap,
  microGap,
  rowPaddingX,
  rowPaddingY,
  ViewModeText,
} from './common'
import { getTupleFields, tupleToRecord } from './logic/tupleFields'
import {
  formatViewValue,
  getValueColorType,
  getViewModeColor,
} from './logic/format'

type ArrayValueViewProps = {
  value: Array<unknown>
  /** Field path, used to recognise arrays the API uses as positional tuples. */
  fieldPath?: string
}

const IDENTITY_KEYS = ['name', 'label', 'title', 'displayName', 'id']

/** A name a reader recognises beats the position in the array. */
const itemIdentity = (
  item: { [key: string]: unknown },
  idx: number,
): string => {
  for (const key of IDENTITY_KEYS) {
    const candidate = item[key]
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate
    }
  }
  return `Item ${idx + 1}`
}

const isPlainObject = (val: unknown): val is { [key: string]: unknown } =>
  typeof val === 'object' && val !== null && !Array.isArray(val)

const ArrayValueView: FC<ArrayValueViewProps> = ({ value, fieldPath }) => {
  const tupleFields = getTupleFields(fieldPath)
  const { darkMode } = useDarkModeInfo()
  const [openItems, setOpenItems] = useState<{ [idx: number]: boolean }>({})

  const renderValue = (val: unknown): ReactNode => {
    if (
      typeof val === 'object' &&
      val !== null &&
      !Array.isArray(val) &&
      Object.keys(val).length > 0
    ) {
      const obj = val as { [key: string]: unknown }
      const entries = Object.entries(obj)
      return (
        <Table>
          {entries.map(([k, val]) => (
            <Row key={k}>
              <Key>{formatColumnHeader(k)}</Key>
              <Value>{renderValue(val)}</Value>
            </Row>
          ))}
        </Table>
      )
    }

    const colorType = getValueColorType(val)
    return (
      <TypedValue $color={getViewModeColor(colorType, darkMode === 'dark')}>
        {formatViewValue(val, colorType)}
      </TypedValue>
    )
  }

  if (!value || value.length === 0) {
    return <ViewModeText>[]</ViewModeText>
  }

  return (
    <Items>
      {value.map((rawItem, idx) => {
        const item =
          tupleFields &&
          Array.isArray(rawItem) &&
          rawItem.length === tupleFields.length
            ? tupleToRecord(rawItem, tupleFields)
            : rawItem

        if (!isPlainObject(item)) {
          return (
            <Item $divided key={idx}>
              {renderValue(item)}
            </Item>
          )
        }

        const isOpen = openItems[idx] ?? false

        const label = itemIdentity(item, idx)

        return (
          <Item key={idx}>
            <ItemToggle
              aria-expanded={isOpen}
              aria-label={label}
              onClick={() =>
                setOpenItems((prev) => ({ ...prev, [idx]: !isOpen }))
              }
              type='button'
            >
              <Icon
                name={isOpen ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
              />
              {label}
            </ItemToggle>
            {isOpen && <ItemBody>{renderValue(item)}</ItemBody>}
          </Item>
        )
      })}
    </Items>
  )
}

export default ArrayValueView

const Items = styled.div`
  display: grid;
`

/* A rule between items, rather than a bar beside each one, so the separation
   reads the same way the field rows above it do. */
const Item = styled.div<{ $divided?: boolean }>`
  display: grid;
  gap: ${microGap};
  padding: ${blockGap} 0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  /* An object item is already boxed, so a rule above it just doubles up. A
     primitive has no box and still needs separating from what precedes it. */
  & + & {
    border-top: ${(p) => (p.$divided ? `1px solid ${p.theme.border}` : 'none')};
  }
`

/* Each item is its own record, so it gets its own box rather than bleeding
   into the array's column. */
const ItemBody = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${radiusRow};
  overflow: hidden;
`

const ItemToggle = styled.button`
  display: flex;
  gap: ${microGap};
  align-items: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  width: 100%;
  text-align: left;
  color: ${(p) => p.theme.text};
  font-size: ${fontSizeValue};
  font-weight: ${fontWeightSemibold};

  &:hover {
    color: ${(p) => p.theme.linkText};
  }
`

/* Mirrors the field rows in DynamicForm so an array item reads as the same
   kind of thing as the object rows around it. */
const Table = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, max-content) 1fr;
  width: 100%;
`

const Row = styled.div`
  display: contents;

  &:last-child > * {
    border-bottom: none;
  }

  &:hover > * {
    background-color: ${(p) => p.theme.rowHover};
  }

  & > * {
    border-bottom: 1px solid ${(p) => p.theme.border};
  }
`

const Key = styled.div`
  border-right: 1px solid ${(p) => p.theme.border};
  padding: ${rowPaddingY} ${rowPaddingX};
  white-space: nowrap;
  color: ${(p) => p.theme.labelText};
  font-size: ${fontSizeLabel};
  font-weight: ${fontWeightMedium};
`

const Value = styled.div`
  padding: ${rowPaddingY} ${rowPaddingX};
  word-break: break-word;
  color: ${(p) => p.theme.text};
  font-size: ${fontSizeValue};
`

const TypedValue = styled.span<{ $color: string }>`
  color: ${(p) => p.$color};
`
